import { Button } from '@components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@components/ui/card'
import InputSearch from '@components/ui/input-search'
import { Label } from '@components/ui/label'
import { Switch } from '@components/ui/switch'
import { useQuery } from '@connectrpc/connect-query'
import { useAuthenticatedMutation } from '@context/sessions'
import { parseErrorMessage } from '@lib/errors'
import {
  Bookmark,
  Heart,
  ShieldCheck,
  Tag,
  User as UserIcon,
  UserCheck,
  UserMinus,
  UserRound,
} from 'lucide-react'
import React, { FC, FormEvent, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  getMyPrivacySettings,
  listMyBlockedUsers,
  unblockUser,
  updateMyPrivacySettings,
} from '@gen/api/v1/api-ApiService_connectquery'
import { User } from '@gen/api/v1/api_pb'
import Title from '@components/title'

const PrivacySettingsView: FC = () => {
  const { data: settingsRes, refetch: refetchSettings } =
    useQuery(getMyPrivacySettings)
  const { data: blockedUsersRes, refetch: refetchBlockedUsers } =
    useQuery(listMyBlockedUsers)
  const unblockUserMutation = useAuthenticatedMutation(unblockUser)
  const updateMyPrivacySettingsMutation = useAuthenticatedMutation(
    updateMyPrivacySettings,
  )

  const [blockedUsers, setBlockedUsers] = useState<User[]>([])
  const [privateBookmarks, setPrivateBookmarks] = useState<boolean>(false)
  const [privateFollowers, setPrivateFollowers] = useState<boolean>(false)
  const [privateLikes, setPrivateLikes] = useState<boolean>(false)
  const [privateProfile, setPrivateProfile] = useState<boolean>(false)
  const [privateTags, setPrivateTags] = useState<boolean>(false)

  const handleUnblockUser = async (userId: string) => {
    try {
      await unblockUserMutation.mutateAsync({ id: userId })

      const { data: blockedUsersRes } = await refetchBlockedUsers()

      if (blockedUsersRes) {
        setBlockedUsers(blockedUsersRes.blockedUsers)
      }

      toast.success('User unblocked successfully')
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to unblock user', {
        description: message,
      })
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    try {
      await updateMyPrivacySettingsMutation.mutateAsync({
        privateBookmarks,
        privateFollowers,
        privateLikes,
        privateProfile,
        privateTags,
      })

      const { data: settingsRes } = await refetchSettings()

      if (settingsRes) {
        setPrivateBookmarks(settingsRes.privateBookmarks)
        setPrivateFollowers(settingsRes.privateFollowers)
        setPrivateLikes(settingsRes.privateLikes)
        setPrivateProfile(settingsRes.privateProfile)
        setPrivateTags(settingsRes.privateTags)
      }

      toast.success('Settings updated successfully')
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to update settings', {
        description: message,
      })
    }
  }

  useEffect(() => {
    if (settingsRes) {
      setPrivateBookmarks(settingsRes.privateBookmarks)
      setPrivateFollowers(settingsRes.privateFollowers)
      setPrivateLikes(settingsRes.privateLikes)
      setPrivateProfile(settingsRes.privateProfile)
      setPrivateTags(settingsRes.privateTags)
    }
  }, [settingsRes])

  useEffect(() => {
    if (blockedUsersRes) {
      setBlockedUsers(blockedUsersRes.blockedUsers)
    }
  }, [blockedUsersRes])

  return (
    <div className="max-w-3xl mx-auto pb-16">
      <Title title="Privacy settings" />

      <div>
        <h1 className="text-3xl font-semibold flex items-center">
          <ShieldCheck className="mr-2" strokeWidth={3} />
          Privacy settings
        </h1>
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Visibility settings</CardTitle>
          <CardDescription>
            Control who can see your profile, posts, activity.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2">
              <Label className="font-semibold">
                <div className="flex items-start">
                  <UserIcon className="h-4 mr-2 w-4" strokeWidth={3} />
                  Private profile
                </div>
                <div className="font-normal text-muted-foreground text-sm mt-2">
                  Only approved followers can see your profile.
                </div>
              </Label>
              <div className="text-right">
                <Switch
                  checked={privateProfile}
                  onCheckedChange={setPrivateProfile}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 mt-6 pt-6 border-t">
              <Label className="font-semibold">
                <div className="flex items-start">
                  <UserCheck className="h-4 mr-2 w-4" strokeWidth={3} />
                  Private followers
                </div>
                <div className="font-normal text-muted-foreground text-sm mt-2">
                  Only you can see who you follow.
                </div>
              </Label>
              <div className="text-right">
                <Switch
                  checked={privateFollowers}
                  onCheckedChange={setPrivateFollowers}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 mt-6 pt-6 border-t">
              <Label className="font-semibold">
                <div className="flex items-start">
                  <Tag className="h-4 mr-2 w-4" strokeWidth={3} />
                  Private tags
                </div>
                <div className="font-normal text-muted-foreground text-sm mt-2">
                  Only you and the user who tagged your can see posts you're
                  tagged in.
                </div>
              </Label>
              <div className="text-right">
                <Switch
                  checked={privateTags}
                  onCheckedChange={setPrivateTags}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 mt-6 pt-6 border-t">
              <Label className="font-semibold">
                <div className="flex items-start">
                  <Heart className="h-4 mr-2 w-4" strokeWidth={3} />
                  Private likes
                </div>
                <div className="font-normal text-muted-foreground text-sm mt-2">
                  Only you can see posts you've liked.
                </div>
              </Label>
              <div className="text-right">
                <Switch
                  checked={privateLikes}
                  onCheckedChange={setPrivateLikes}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 mt-6 pt-6 border-t">
              <Label className="font-semibold">
                <div className="flex items-start">
                  <Bookmark className="h-4 mr-2 w-4" strokeWidth={3} />
                  Private bookmarks
                </div>
                <div className="font-normal text-muted-foreground text-sm mt-2">
                  Only you can see posts you've bookmarked.
                </div>
              </Label>
              <div className="text-right">
                <Switch
                  checked={privateBookmarks}
                  onCheckedChange={setPrivateBookmarks}
                />
              </div>
            </div>

            <div className="mt-8 text-right w-full">
              <Button
                disabled={
                  privateBookmarks === settingsRes?.privateBookmarks &&
                  privateFollowers === settingsRes?.privateFollowers &&
                  privateLikes === settingsRes?.privateLikes &&
                  privateProfile === settingsRes?.privateProfile &&
                  privateTags === settingsRes?.privateTags
                }
                type="submit"
              >
                Save changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Blocked users</CardTitle>
          <CardDescription>Manage users you've blocked.</CardDescription>
        </CardHeader>
        <CardContent>
          <InputSearch type="text" placeholder="Search" />

          <div className="mt-6 w-full">
            {blockedUsers && blockedUsers.length > 0 ? (
              <div>
                {blockedUsers.map((user) => (
                  <div key={user.id} className="flex items-center py-2">
                    <div className="flex items-center">
                      {user.profilePictureUrl ? (
                        <img
                          src={user.profilePictureUrl}
                          alt={user.username}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center overflow-hidden">
                          <UserRound className="h-10 w-10 text-muted-foreground" />
                        </div>
                      )}

                      <div className="ml-4 ">
                        <p className="font-semibold text-sm">
                          @{user.username}
                        </p>
                        {user.displayName && (
                          <p className="text-muted-foreground text-xs">
                            {user.displayName}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="ml-auto">
                      <Button
                        onClick={() => handleUnblockUser(user.id)}
                        size="sm"
                        variant="secondary"
                      >
                        <UserMinus strokeWidth={2} />
                        Unblock
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted-foreground text-sm">
                No blocked users.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default PrivacySettingsView
